/**
 * Buying credits, in a browser.
 *
 * Stripe Checkout, never RevenueCat: react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. The server owns the money — POST /billing/checkout
 * mints a session bound to the caller's Cognito sub, and the signed webhook is what
 * actually grants credits. This only asks for the session and hands the browser over.
 */
import { ApiError, request } from './client';

export type CheckoutResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  /**
   * `reason` is the server's refusal token when there was one. `already_subscribed` is worth
   * acting on and not only showing: it can mean the server has just confirmed, with Stripe, a
   * subscription this screen did not know of — see Credits.tsx.
   */
  | { status: 'error'; message: string; reason?: string };

/**
 * What is being bought.
 *
 * A subscription for somebody with no plan; the one-time top-up for a subscriber whose month
 * has run out. "Get more credits" only ever asked for the first, so a subscriber bought a
 * SECOND $20 plan, and the first cancellation of the two then dropped them to free while they
 * paid for the other. The server now refuses that, and this names what is wanted.
 */
export type CheckoutKind = 'subscription' | 'topup';

/*
 * The server's refusals are machine tokens, which `client.ts` puts in `reason` — and a 409's
 * message IS that token. Shown as they are, "already_subscribed" is a word nobody can act on in
 * the one place that asks for money.
 */
const CHECKOUT_REFUSALS: Record<string, string> = {
  already_subscribed: 'You already have a subscription. Extra credits are the way to get more this month.',
  topup_unavailable: "Extra credits aren't on sale yet. Your credits refill when your plan renews.",
  // Two presses at once — two tabs, or a phone and the web: the server makes one session (review, 2026-09-30).
  checkout_in_progress: 'A checkout is already opening in another tab or on another device. Finish it there, or try again in a minute.',
  // The trial is not named here: it is one per account (ac26ee7), so this is the sentence for an
  // account that may not have one. `refusalFor` names it only when the caller knows it can.
  subscription_required: 'Extra credits are for subscribers. Subscribe to get 20,000 credits a month.',
};

/**
 * The refusal in words, for this account. A top-up refused for want of a subscription promised
 * "the 7-day free trial" to everybody, including an account that had had its week and would be
 * charged on the spot (F23, 2026-09-30). The trial is named only when `trialAvailable` is true;
 * absent promises nothing.
 */
function refusalFor(reason: string, options: CheckoutOptions): string | undefined {
  if (reason === 'subscription_required' && options.trialAvailable === true) {
    return 'Extra credits are for subscribers. Start the 7-day free trial to get 20,000 credits a month.';
  }
  return CHECKOUT_REFUSALS[reason];
}

/**
 * `resume: 'build'` is the Plan screen's Build button: the server sends that checkout back to
 * /billing/success?…&resume=build, which starts the build from the draft put away before the
 * redirect, and a cancel back to /new/plan?checkout=cancelled. The shared contract with the
 * server (2026-09-29): `{ kind: 'subscription', resume?: 'build' }`, and every subscription
 * checkout is a 7-day trial with the card taken up front, whoever asks.
 */
export interface CheckoutOptions {
  resume?: 'build';
  /** From /credits: whether this account can still have the free week. Only words depend on it. */
  trialAvailable?: boolean;
}

export async function startCheckout(
  kind: CheckoutKind,
  redirect: (url: string) => void = (url) => window.location.assign(url),
  options: CheckoutOptions = {},
): Promise<CheckoutResult> {
  try {
    const body = options.resume ? { kind, resume: options.resume } : { kind };
    const { url } = await request<{ url?: string }>('/billing/checkout', { method: 'POST', body });
    // Navigating to undefined would land someone on a blank page mid-purchase.
    if (!url) return { status: 'error', message: 'Could not start checkout.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (err instanceof ApiError) {
      // A dead button is worse than a prompt they can act on.
      if (err.kind === 'unauthorized') return { status: 'signin_required' };
      const refusal = err.reason ? refusalFor(err.reason, options) : undefined;
      return { status: 'error', message: refusal ?? err.message, ...(err.reason ? { reason: err.reason } : {}) };
    }
    return { status: 'error', message: 'Could not start checkout.' };
  }
}

export interface CheckoutOffers {
  /** Whether a top-up is on sale, and — when the server says — how many credits it adds. */
  topup: { available: boolean; credits?: number };
}

/**
 * What is on sale, asked BEFORE a button is drawn for it: `GET /billing/checkout`.
 *
 * The top-up exists only once the owner has created its one-time price in Stripe. Until then a
 * "Buy 10,000 credits" button could only answer "not available" after it was pressed. Any
 * failure — no connection, a server deployed before this route existed — reads as NOT on sale:
 * the absent case is the safe one, because a missing button costs nothing and a broken one
 * costs trust. Not retried, for the same reason.
 */
export async function checkoutOffers(): Promise<CheckoutOffers> {
  try {
    const data = await request<{ topup?: { available?: unknown; credits?: unknown } }>('/billing/checkout', {
      method: 'GET',
      retry: false,
    });
    const topup = data?.topup;
    if (topup?.available !== true) return { topup: { available: false } };
    return { topup: { available: true, ...(typeof topup.credits === 'number' ? { credits: topup.credits } : {}) } };
  } catch {
    return { topup: { available: false } };
  }
}

/**
 * The balance, and whether the free first build is still there.
 *
 * `firstBuildDone` has always been in this response and this client dropped it on the floor
 * — the same shape as the phone's bug, where the app could not say "this one is free"
 * because it had never read the field that says so. Optional in the type because an older
 * server would not send it, and absent must read as "unknown" rather than as "spent".
 *
 * `subscribed` is whether a store is BILLING the account, which `tier` does not say: a promo code
 * puts an account on the paid tier with no plan behind it. What to sell somebody — Subscribe or
 * the top-up — is decided on this. Optional for the same reason, and absent is "not known".
 */
export interface CreditBalance {
  creditsRemaining: number;
  tier: string;
  firstBuildDone?: boolean;
  subscribed?: boolean;
  /**
   * Whether the next subscription checkout is the free week: one per account, so an account that
   * has had it is sold the plan from day one. Absent — a server from before the field — is the
   * trial (state/trial.ts `offerFor`).
   */
  trialAvailable?: boolean;
}

export async function creditBalance(): Promise<CreditBalance> {
  return request<CreditBalance>('/credits');
}

export type PortalResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  /** No Stripe customer at all — they subscribed through the App Store. A normal answer. */
  | { status: 'managed_elsewhere' }
  | { status: 'error'; message: string };

/**
 * The door out. Opens Stripe's billing portal so a subscriber can actually cancel.
 *
 * This closes a promise the product was already making: strings.ts has been telling web
 * customers they can "cancel anytime from your account" with nothing behind it. A
 * subscription you cannot leave from where you were told you could leave it is the kind of
 * thing that is both a support burden and, in several places, a legal one.
 *
 * Three answers that are not errors, and one that is:
 *  - 404 `no_stripe_subscription` means this person pays through the App Store. That is a
 *    FACT about them, not a failure, so they are told where to go rather than shown a
 *    problem to solve.
 *  - 502 carries Stripe's own sentence, which is the useful part: the common one is "No
 *    configuration provided", meaning the portal is not enabled in the Stripe dashboard.
 *    Replacing that with "the server had a problem" would hide the one instruction anybody
 *    could act on, so it is surfaced verbatim.
 *  - 401 asks them to sign in, rather than presenting a dead button.
 *
 * Someone who subscribed before the customer id was stored is found by an email lookup on
 * the first visit and remembered after that, so the FIRST call for such a person is slow.
 * That is why this gets a longer deadline than an ordinary data call.
 */
const PORTAL_TIMEOUT_MS = 30_000;

export async function openBillingPortal(
  redirect: (url: string) => void = (url) => window.location.assign(url),
): Promise<PortalResult> {
  try {
    const { url } = await request<{ url?: string }>('/billing/portal', {
      method: 'POST',
      timeoutMs: PORTAL_TIMEOUT_MS,
    });
    if (!url) return { status: 'error', message: 'Could not open the billing portal.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'error', message: 'Could not open the billing portal.' };
    if (err.kind === 'unauthorized') return { status: 'signin_required' };
    /*
     * Read off `reason`, not `code`: this route puts its machine token in the body's
     * `error` field, which client.ts warns is used BOTH ways across the API. Branching on
     * `code` here would never fire, and everyone would be told to check their connection.
     */
    if (err.kind === 'not_found' && err.reason === 'no_stripe_subscription') {
      return { status: 'managed_elsewhere' };
    }
    // Stripe's own words for a gateway failure; ours for anything else.
    if (err.status === 502 && err.reason) return { status: 'error', message: err.reason };
    return { status: 'error', message: err.message };
  }
}

export type RedeemResult =
  /**
   * `creditsRemaining` is the balance AFTER the code, when the server says it. Absent is left
   * absent rather than read as 0: a screen showing "0" to someone who just added credits is the
   * bug the Credits screen's "—" exists to prevent.
   */
  | { status: 'redeemed'; creditsRemaining?: number; granted: number; tier?: string }
  /**
   * `reason` is the server's own token ('invalid_code', 'already_redeemed', 'code_expired',
   * 'code_exhausted', 'promo_unavailable', 'signup_required') or 'rate_limited', or the client's
   * error kind for anything else. The screen turns it into words with the phone's own
   * `redeemFailureMessage`, which answers an unknown one with the generic sentence.
   */
  | { status: 'refused'; reason: string };

/**
 * Redeem a promo code: `POST /redeem` with `{ code }`, the call the phone makes (guidedClient.ts).
 *
 * On the LLM Function URL, not the API gateway: /redeem is a route of the LLM proxy and the
 * gateway has no resource for it, so `base: 'api'` would answer 403/404 for every code. The phone
 * reaches it the same way, through EXPO_PUBLIC_LLM_URL.
 *
 * Sent once and never retried (the client only repeats a GET), though the server would refuse a
 * second redemption anyway: it claims a per-account marker before it grants anything.
 */
export async function redeemCode(code: string): Promise<RedeemResult> {
  try {
    const data = await request<{ creditsRemaining?: number; granted?: number; tier?: string }>('/redeem', {
      base: 'llm',
      method: 'POST',
      body: { code: code.trim() },
    });
    return {
      status: 'redeemed',
      granted: data?.granted ?? 0,
      ...(typeof data?.creditsRemaining === 'number' ? { creditsRemaining: data.creditsRemaining } : {}),
      ...(data?.tier ? { tier: data.tier } : {}),
    };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'refused', reason: 'unknown' };
    /*
     * The status decides these two, whatever the body says: a 401 from this route is always
     * "no account", and a 429 may come from Lambda's own throttle, whose body is not ours.
     */
    if (err.kind === 'unauthorized') return { status: 'refused', reason: 'signup_required' };
    if (err.kind === 'rate_limited') return { status: 'refused', reason: 'rate_limited' };
    // The machine token is in the body's `error` field on this route, which the client keeps
    // as `reason` (see client.ts on why that field is read per route).
    return { status: 'refused', reason: err.reason ?? err.kind };
  }
}
